/**
 * SyncSummary Section Component
 *
 * Displays sync operations summary with totals and by-system breakdown.
 */

import React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography
} from '@mui/material'
import SyncIcon from '@mui/icons-material/Sync'
import { SyncSummary as SyncSummaryType } from '../../../../../services/reportsApi'
import Metric from '../Metric'

interface SyncSummaryProps {
  data: SyncSummaryType
}

const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'm'
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
  }
  return num.toString()
}

const SyncSummary: React.FC<SyncSummaryProps> = ({ data }) => {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <SyncIcon color="primary" />
          <Typography variant="h6" component="h2">
            Sync Summary
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={2.4}>
            <Metric label="Total" value={data.total} />
          </Grid>
          <Grid item xs={2.4}>
            <Metric
              label="Completed"
              value={data.completed}
              color="#2e7d32"
            />
          </Grid>
          <Grid item xs={2.4}>
            <Metric
              label="Failed"
              value={data.failed}
              color="#d32f2f"
            />
          </Grid>
          <Grid item xs={2.4}>
            <Metric
              label="Running"
              value={data.running}
              color="#1976d2"
            />
          </Grid>
          <Grid item xs={2.4}>
            <Metric
              label="Records"
              value={formatNumber(data.records_processed)}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          By System
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {Object.entries(data.by_system).map(([system, systemData]) => (
            <Chip
              key={system}
              label={`${system}: ${systemData.completed}/${systemData.total}`}
              size="small"
              color={systemData.failed > 0 ? 'error' : 'default'}
              variant="outlined"
            />
          ))}
        </Stack>

        <Box sx={{ mt: 2, textAlign: 'right' }}>
          <Button
            component={RouterLink}
            to="/admin/data-sync/history"
            size="small"
          >
            View Sync History
          </Button>
        </Box>
      </CardContent>
    </Card>
  )
}

export default SyncSummary
